import { session } from 'services/session';
import { Columns } from '../megatable/column-service';
import { Networks } from '../megatable/models';
import placeholderImage from '../../../images/product_placeholder.png';

/* Multi-org support */
const checkAllSites = (params) => {
	if (session.allSites) {
		const additionalOrgs = session.getAccessListIds();
		const role = session.isAdmin
			? session.allPubs ? 'publishers' : 'merchants'
			: session.userRole;
		if (additionalOrgs) {
			params[
				role === 'merchants' ? 'additional_merchs' : 'additional_pubs'
			] = additionalOrgs;
		}
	}
	return params;
};

/* Pagination helpers */
const invalidPage = (page, totalItems, limit) => {
	const lastPage = Math.ceil(totalItems / limit);

	if (lastPage && page > lastPage) return true;
	return false;
};

const calculatePagination = (pageNum, perPage, limit) => {
	/*
	 * We use a limit of 500 for stats queries, which doesn't directly
	 * correspond with the frontend perPage. Need to calculate the offset
	 * for the stats query, or where to fetch from the existing stats.
	 */
	const offset = (pageNum - 1) * perPage;
	const page = Math.floor(offset / limit) + 1;
	// Indexes of the requested stats in the array of 500 stat objects
	const indexStart = offset % limit;
	const indexEnd = indexStart + perPage;

	return [page, indexStart, indexEnd];
};

/* Formatting functions */
const formatNumberWithCommas = (number) => {
	let numString = `${number}`;
	if (number >= 1000 || number <= -1000) {
		const thousandRegex = /(-?\d+)(\d{3})/;
		while (thousandRegex.test(numString)) {
			numString = numString.replace(thousandRegex, '$1,$2');
		}
	}
	return numString;
};

const formatNumber = (number, type, columnName) => {
	const {roundedFields, roundedFieldsLessThanOne} = new Columns();
	let numString;
	number = parseFloat(number);

	/*
	 * Depending on column type leading 0 is removed or
	 * Keep leading 0 if number is less than 1 (0.67 => 0.67)
	 * Round number if greater than 1 or equal to 0.
	 */
	if (
		number > 1 && roundedFields.includes(columnName)
		|| roundedFieldsLessThanOne.includes(columnName)
	) {
		number = Math.round(number);
	}

	/*
	 * Values > 1 should have 2 decimal places of precision by default.
	 * Rounded decimal fields should be 2 places of precision
	 */
	if (['currency', 'percent', 'roi'].includes(type) && (
		(!roundedFields.includes(columnName) && number > 0) ||
		(roundedFields.includes(columnName) && Math.abs(number) > 0 && Math.abs(number) < 1) ||
		(roundedFields.includes(columnName) && number < 0))
	) {
		numString = number.toFixed(2);
	} else if (columnName === 'roi_goal' || columnName === 'roi_actual') {
		numString = number.toFixed(1);
	} else if (roundedFields.includes(columnName) && number > 1) {
		numString = number.toFixed(0);
	} else {
		numString = (number || 0).toString();
	}

	if (columnName && columnName.includes('percent_difference')) {
		numString = number.toFixed(0);
	}

	numString = formatNumberWithCommas(numString);

	/*
	 * Add trailing zero to currency values less than $1 ($0.4 => $0.40)
	 * Values greater than 1 will have been rounded above.
	 */
	if (['currency', 'roi'].includes(type) && numString.match(/0\.\d$/)) {
		numString += '0';
	}

	return numString;
};

const formatCurrencyThousands = (number, unit) => {
	// Remove commas, dollar sign, etc
	number = parseFloat(number.split('').reduce((acc, curr) => /[\d.]/g.test(curr) ? acc + curr : acc, ''));
	if (number < 1000) {
		return `$${number}`;
	}

	number = Math.round(number);
	const labels = ['', 'K', 'M', 'B'];
	let thousands = 0;

	while (number >= 1000 && thousands < labels.length - 1) {
		if (labels[thousands] === unit) {
			break;
		}
		number /= 1000;
		thousands++;
	}

	// Remove trailing 0's after decimal and the decimal itself, adds commas back in
	let numberString = number.toFixed(1);
	while (numberString.includes('.') && ['0', '.'].includes(numberString[numberString.length - 1])) {
		numberString = numberString.substring(0, numberString.length - 1);
	}
	numberString = formatNumberWithCommas(numberString);

	return `$${numberString}${thousands ? labels[thousands] : ''}`;
};

const formatString = (value) => {
	const valueMap = {
		'POST_CLICK': 'Post Click',
		'LAST_CLICK': 'Last Click',
		'MULTIPLE': 'Multiple',
		'Unknown': '-',
	};

	return valueMap[value] || value;
};

const formatValue = (value, type, columnName, convertToDashes=false) => {
	const zeroAllowedColumns = ['daily_budget_amount', 'monthly_budget_amount', 'publisher_revenue'];
	if (type === 'string') return formatString(value);

	// Return '-' for falsy numerical values like 0 or NaN
	if (convertToDashes && !parseFloat(value) && !zeroAllowedColumns.includes(columnName)) return '-';

	value = formatNumber(value, type, columnName); // Returns number as string
	switch (type) {
		case 'currency':
			// If negative value, format it as -$x.xx rather than $-x.xx
			return value[0] === '-' ? `${value[0]}$${value.slice(1)}` : `$${value}`;
		case 'percent':
			return `${value}%`;
		case 'multiple':
			return `${value}x`;
		default:
			return value;
	}
};

const titleCase = (name) => {
	return name.replace(/(?:^|([ \t([]))([a-z])/g, (match, value1, value2) => {
		return `${value1 || ''}${value2.toUpperCase()}`;
	});
};

const camelCaseToDash = (name) => {
	return name.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`);
};

const displayNetworks = (networkList, type) => {
	// TODO: This may be deprecated as it's removed from megatable
	/*
	 * Returns formatted networks for either a tooltip ('Amazon', 'Narrativ') or text
	 * for the table cell ('Narrativ' / 'Narrativ - Rakuten'). Compares networkList to '-'
	 * due to edge case when totals for total-row haven't been calculated
	 */
	if (!networkList || networkList === '-') return '';

	const networks = new Networks().defaultNetworks;
	networks.push({label: 'Amazon', name: 'amazon', selected: false});

	if (type === 'tooltip') {
		const formattedNetworkList = networkList.map((network) => {
			const networkMatch = networks.find((item) => item.name === network);
			return networkMatch.label;
		});
		return formattedNetworkList.join(', ');
	} else if (type === 'label') {
		if (networkList.length === 1) {
			const networkMatch = networks.find((item) => item.name === networkList[0]);
			return networkMatch.label;
		} else {
			return 'Multiple';
		}
	}
};

const getNetworkQueryParam = (networks) => {
	// Returns selected networks formatted for api, ex: 'narrativ,rakuten'
	return networks.reduce((agg, network) => {
		if (network.selected) agg += `${network.name},`;
		return agg;
	}, '').slice(0, -1);
};

const getSelectedString = (options) => {
	return options.reduce((options, option) => (
		option.selected ? options.concat([option.label]) : options
	), []).join(',');
};

// Converts TitleCase to snake_case
const snakeCase = (name) => {
	return name.replace(/([^_A-Z])([A-Z])/g, '$1_$2').toLowerCase();
};

/* Service functions */
const buildQueryString = (params) => {
	// {key1: value1, key2: value2} => '?key1=value1&key2=value2'
	let queryStr = '?';
	Object.keys(params).forEach((key, i) => {
		queryStr += `${i === 0 ? '' : '&'}${key}=${params[key]}`;
	});

	return queryStr;
};

const getStatsFromCache = (statCache, page, indexStart, indexEnd) => {
	if (!statCache.pages[page]) return [];

	return Promise.resolve(statCache.pages[page].slice(indexStart, indexEnd));
};

const orderParams = (params, orderedKeys) => {
	// Order params so they're consistent & easier to mock in tests
	const orderedParams = {};

	orderedKeys.forEach((key) => {
		if (params[key]) {
			orderedParams[key] = params[key];
		}
	});

	return orderedParams;
};

/* DOM functions */
const handleImageError = (e, logoUrl) => {
	e.target.onerror = null;
	e.target.src = logoUrl ? logoUrl : placeholderImage;
};

const scrollPage = (selector, dimension, distance) => {
	const element = document.querySelector(selector);
	element[dimension] = distance;
};

export {
	buildQueryString,
	calculatePagination,
	camelCaseToDash,
	checkAllSites,
	displayNetworks,
	formatNumber,
	formatCurrencyThousands,
	formatString,
	formatValue,
	getNetworkQueryParam,
	getSelectedString,
	getStatsFromCache,
	handleImageError,
	invalidPage,
	orderParams,
	scrollPage,
	snakeCase,
	titleCase,
};
